/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../fragments'

import {
  Button,
  Input,
  SelectWithData,
  Spinner,
  SpanWithData,
  ConsolidationBankAccountDisplay as BankAccountDisplay
} from '../'

export class ConsolidationBankAccount extends React.Component {
  static propTypes = {
    consolidation: PropTypes.string.isRequired,
    bankAccountRef: PropTypes.string.isRequired,
    bankAccount: PropTypes.object
  }

  constructor(props) {
    super(props)

    this.state = {
      editBankAccount: false,
      addBankAccount: false,
      bankName: 'Loading',
      beneficiaryName: 'Loading',
      accountType: 'Loading',
      accountNumber: 'Loading',
      bankAccountRef: props.bankAccountRef || '',
      default: false,
      errorDefaultBankAccount: false
    }
  }

  UNSAFE_componentWillReceiveProps(props) {
    if (props.data.consolidation) {
      this.setState({
        accountNumber: props.data.consolidation.bankaccount ? props.data.consolidation.bankaccount.accountNumber : undefined,
        bankName: props.data.consolidation.bankaccount ? props.data.consolidation.bankaccount.bankName : null,
        beneficiaryName: props.data.consolidation.bankaccount ? props.data.consolidation.bankaccount.beneficiaryName : undefined,
        accountType: props.data.consolidation.bankaccount ? props.data.consolidation.bankaccount.accountType : null
      })
    }
  }

  goEditBankAccount = (bankAccountToEdit) => {
    if (bankAccountToEdit) {
      this.setState({
        editBankAccount: true,
        addBankAccount: false,
        bankName: bankAccountToEdit.bankName,
        beneficiaryName: bankAccountToEdit.beneficiaryName,
        accountType: bankAccountToEdit.accountType,
        accountNumber: bankAccountToEdit.accountNumber,
        default: false,
        errorDefaultBankAccount: false
      })
    } else {
      this.setState({
        editBankAccount: true,
        addBankAccount: false,
        errorDefaultBankAccount: false
      })
    }
  }

  goAddBankAccount = () => {
    this.setState({
      editBankAccount: false,
      addBankAccount: true,
      bankName: '',
      beneficiaryName: '',
      accountType: '',
      accountNumber: '',
      bankAccountRef: '',
      default: false
    })
  }

  goReadOnly = (bankAccount) => {
    this.setState({
      editBankAccount: false,
      addBankAccount: false,
      bankAccountToEdit: null
    })

    if (this.props.refresh) {
      if (!bankAccount) {
        this.props.refresh(this.props.data.consolidation.bankaccount)
      } else {
        this.props.refresh(bankAccount)
      }
    }
  }

  canSave = (bankAccount) => {
    return bankAccount.bankName !== this.state.bankName
      || bankAccount.beneficiaryName !== this.state.beneficiaryName
      || bankAccount.accountType !== this.state.accountType
      || bankAccount.accountNumber !== this.state.accountNumber
      || this.state.bankAccountRef !== this.props.bankAccountRef
  }

  handleSave = () => {
    if (this.state.editBankAccount) {
      // Edit
      let consolidationBankAccount = this.props.data.consolidation.bankaccount

      if (this.canSave(consolidationBankAccount)) {
        let bankAccountInfo = {
          id: consolidationBankAccount.id
        }

        if (consolidationBankAccount.bankName !== this.state.bankName) {
          let bankName = this.state.bankName.toLowerCase()
          bankAccountInfo.bankName = bankName
          bankAccountInfo.branchCode = this.getUniversalBankBranchCode(bankName)
        }

        if (consolidationBankAccount.beneficiaryName !== this.state.beneficiaryName) {
          bankAccountInfo.beneficiaryName = this.state.beneficiaryName
        }

        if (consolidationBankAccount.accountType !== this.state.accountType)
          bankAccountInfo.accountType = this.state.accountType

        if (consolidationBankAccount.accountNumber !== this.state.accountNumber)
          bankAccountInfo.accountNumber = this.state.accountNumber

        this.props.updateBankAccount({ variables: { input: bankAccountInfo } })
        .then(({ data }) => {
          this.props.updateConsolidationBankAccount({
            variables: {
              input: {
                id: this.props.consolidationId,
                bankaccountRef: this.state.bankAccountRef,
                bankaccount: consolidationBankAccount.id
              }
            }
          })
          .then(() => {
            this.props.data.refetch()
            .then(this.goReadOnly({
              id: this.state.accountToEdit,
              bankName: this.state.bankName,
              beneficiaryName: this.state.beneficiaryName,
              accountType: this.state.accountType,
              accountNumber: this.state.accountNumber,
              default: false
            }))
          })
          .catch((error) => console.log('there was an error updating consolidation bank account', error))
        })
        .catch((error) => {
          console.log('there was an error editing bank account', error);
        })
      } else {
        this.goReadOnly()
      }
    } else {
      // Add
      let bankName = this.state.bankName.toLowerCase()
      let bankAccountInfo = {
        bankName: bankName,
        beneficiaryName: this.state.beneficiaryName,
        accountType: this.state.accountType,
        accountNumber: this.state.accountNumber,
        default: false,
        branchCode: this.getUniversalBankBranchCode(bankName),
      }

      this.props.updateBankAccount({ variables: { input: bankAccountInfo } })
        .then(({ data }) => {
          let bankaccount = data.bankaccountMutate.bankaccount.id
          this.props.updateConsolidationBankAccount({
            variables: {
              input: {
                id: this.props.consolidationId,
                bankaccountRef: this.state.bankAccountRef,
                bankaccount
              }
            }
          })
          .then(() => {
            this.props.data.refetch()
            .then(this.goReadOnly(bankAccountInfo))
          })
          .catch((error) => console.log('there was an error updating consolidation bank account', error))
        })
        .catch((error) => console.log('there was an error adding bank account', error))
    }
  }

  getUniversalBankBranchCode = (bankName) => {
    return {
      'absa': '632005',
      'african_bank': '430000',
      'bidvest': '462005',
      'capitec': '470010',
      'finbond': '589000',
      'fnb': '250655',
      'grindrod': '584000',
      'grobank': '410506',
      'nedbank': '198765',
      'postbank': '460005',
      'sasfin': '683000',
      'standard_bank': '051001',
      'tymebank': '678910',
    }[bankName]
  }

  handleReferenceInput = e => {
    let currentRefInputValue = e.target.value
    if (currentRefInputValue.length > 20) {
      this.setState({
        bankAccountRef: currentRefInputValue,
        bankAccountRefLengthError: true
      })
    } else {
      this.setState({
        bankAccountRef: currentRefInputValue,
        bankAccountRefLengthError: false })
    }
  }

  getBankAccountRecord = (bankAccount) => {
    let template = [
      <div className='slds-grid slds-size_1-of-1'>
        <div className='slds-size_1-of-3'>Bank name</div>
        <SelectWithData
          id='bankName'
          forField='BankAccountBankName'
          value={this.state.bankName}
          onChange={e => this.setState({ bankName: e.target.value })}
          className='slds-size_1-of-3'
        />
      </div>,
      <div className='slds-grid slds-size_1-of-1'>
        <div className='slds-size_1-of-3'>Beneficiary name</div>
        <Input
          id='beneficiaryName'
          value={this.state.beneficiaryName}
          placeholder=''
          onChange={e => this.setState({ beneficiaryName: e.target.value })}
        />
      </div>,
      <div className='slds-grid slds-size_1-of-1'>
        <div className='slds-size_1-of-3'>Account type</div>
        <SelectWithData
          id='accountType'
          forField='BankAccountAccountType'
          value={this.state.accountType}
          onChange={e => this.setState({ accountType: e.target.value })}
        />
      </div>,
      <div className='slds-grid slds-size_1-of-1'>
        <div className='slds-size_1-of-3'>Account number</div>
        <Input
          id='accountNumber'
          value={this.state.accountNumber}
          placeholder=''
          onChange={e => this.setState({ accountNumber: e.target.value })}
        />
      </div>,
      <div className='slds-grid slds-size_1-of-1'>
        <div className='slds-size_1-of-3'>References</div>
        <Input
          id='reference'
          value={this.state.bankAccountRef}
          placeholder=''
          onChange={this.handleReferenceInput}
          hasError={this.state.bankAccountRefLengthError}
          errorMessageTooltip={`Reference can be at most 20 characters long (it has ${this.state.bankAccountRef.length}).`}
        />
      </div>,
      <div>
        <Button
          onClick={this.handleSave}
          disabled={this.state.bankName === null ||
            this.state.errorDefaultBankAccount ||
            this.state.bankAccountRefLengthError}>
          Save
        </Button>
        <Button onClick={this.goReadOnly}>
          Cancel
        </Button>
      </div>
    ]

    if(this.state.addBankAccount) {
      // blank for addition
      return template
    } else {
      return this.state.editBankAccount
        ? template
        : bankAccount
          ? [
              <SpanWithData forField='BankAccountBankName' searchKey={bankAccount.bankName} />,
              bankAccount.beneficiaryName,
              <SpanWithData forField='BankAccountAccountType' searchKey={bankAccount.accountType} />,
              bankAccount.accountNumber,
              this.state.bankAccountRef,
              <Button
                onClick={e => this.goEditBankAccount(bankAccount)}>
                Edit
              </Button>
            ]
          : null
    }
  }

  render() {
    if (this.props.data.loading) {
      return <Spinner className='slds-is-relative' />
    }

    if (this.props.data.error) {
      return 'Oh dear! We have an issue'
    }

    let bankAccountRecord = this.getBankAccountRecord(this.props.data.consolidation.bankaccount)

    if (this.props.tableRowView && !this.state.editBankAccount && !this.state.addBankAccount) {
      return (
        <React.Fragment>
          {this.state.bankName
            ? `${this.state.accountNumber} (${this.state.bankName})`
            : 'No bank account details currently specified'
          }
          <span className='slds-p-left_medium'>
            <Button
              onClick={() => this.state.bankName
                ? this.setState({ editBankAccount: true })
                : this.setState({ addBankAccount: true })
              }>
              {this.state.bankName ? 'Edit' : 'Add'}
            </Button>
          </span>
        </React.Fragment>
      )
    } else {
      return (
        <BankAccountDisplay
          data={bankAccountRecord}
          goReadOnly={this.goReadOnly}
          goEdit={this.goEditBankAccount}
          goAdd={this.goAddBankAccount}
          addMode={this.state.addBankAccount || bankAccountRecord}
        />
      )
    }
  }
}

export const getConsolidationBankAccount = gql`
  query getConsolidationBankAccount($id: ID!) {
    consolidation(id: $id) {
      id
      bankaccountRef
      bankaccount {
        ...BankAccount
      }
    }
  }
  ${fragments.bankAccount}
`

const updateBankAccount = gql`
  mutation updateBankAccount($input: BankAccountMutationInput!) {
    bankaccountMutate(input: $input) {
      bankaccount {
        ...BankAccount
      }
    }
  }
  ${fragments.bankAccount}
`

const updateConsolidationBankAccount = gql`
  mutation updateConsolidationBankAccount($input: ConsolidationMutationInput!) {
    consolidationMutate(input: $input) {
      consolidation {
        id
        bankaccountRef
        bankaccount {
          ...BankAccount
        }
      }
    }
  }
  ${fragments.bankAccount}
`

export const withData = graphql(getConsolidationBankAccount, {
  options: (ownProps) => ({
    variables: {
      id: ownProps.consolidationId
    }
  })
})

export const withBankAccountMutate = graphql(updateBankAccount, {
  name: 'updateBankAccount'
})

export const withConsolidationMutate = graphql(updateConsolidationBankAccount, {
  name: 'updateConsolidationBankAccount'
})

export default compose(
  withData,
  withBankAccountMutate,
  withConsolidationMutate
)(ConsolidationBankAccount)



// WEBPACK FOOTER //
// ./src/components/consolidation-bankaccount/index.js